// Session refresh + global 401 handling for every ops-portal module.
//
// The access token (localStorage, 1 h) is sent as "Authorization: Bearer".
// The refresh token lives in an HttpOnly cookie on the auth domain
// (auth.littlescholarsnyc.com) that JavaScript cannot read.
//
// installFetchInterceptor() wraps window.fetch once, so no module api.ts needs
// to change: when any call that carried a Bearer token comes back 401, it
// refreshes the session (once, shared by parallel calls) and repeats the call
// with the new token. If the refresh is refused, the session-expired handler
// signs the person out and the login screen appears.

export const TOKEN_KEY = 'ls_ops_staff_token'

// Preview as (Kate, 2026-10-18): a 30-minute token for another person, kept for this tab only. While it is set,
// every call goes out with it instead of the person's own token, and nothing is saved: any request that would change
// data is answered here, in the browser, with 403 — no backend ever sees it.
export const PREVIEW_KEY = 'ls_ops_preview_token'

export function previewToken(): string | null {
  try {
    return sessionStorage.getItem(PREVIEW_KEY)
  } catch {
    return null
  }
}

export function setPreviewToken(token: string | null): void {
  try {
    if (token) sessionStorage.setItem(PREVIEW_KEY, token)
    else sessionStorage.removeItem(PREVIEW_KEY)
  } catch {
    /* no session storage — preview can't start */
  }
}

let onPreviewExpired: (() => void) | null = null
export function setPreviewExpiredHandler(fn: (() => void) | null): void {
  onPreviewExpired = fn
}

function previewBlocked(): Response {
  return new Response(JSON.stringify({ detail: 'Preview mode — nothing is saved. Leave the preview to make changes.' }), {
    status: 403,
    headers: { 'Content-Type': 'application/json' },
  })
}

const AUTH_BASE: string =
  import.meta.env.VITE_ENROLLMENT_API_BASE ?? 'https://ops-enrollment-api-REPLACE-uc.a.run.app'

export interface RefreshResult {
  access_token: string
  email: string
  name: string
  role: string
  location?: string
}

const nativeFetch: typeof fetch = window.fetch.bind(window)

let inFlight: Promise<RefreshResult | null> | null = null
let onExpired: (() => void) | null = null
let installed = false

export function setSessionExpiredHandler(fn: (() => void) | null): void {
  onExpired = fn
}

/** null = no valid session (sign in again). Throws on network / 5xx, so a
 *  temporary outage does not sign anybody out. */
export function refreshSession(): Promise<RefreshResult | null> {
  if (!inFlight) {
    inFlight = (async () => {
      const tokenBefore = localStorage.getItem(TOKEN_KEY)
      const res = await nativeFetch(`${AUTH_BASE}/staff/refresh`, { method: 'POST', credentials: 'include' })
      if (res.status === 401 || res.status === 403) {
        // Only drop the token this refresh was meant to replace - a sign-in
        // that finished meanwhile has stored a new, valid one.
        if (localStorage.getItem(TOKEN_KEY) === tokenBefore) localStorage.removeItem(TOKEN_KEY)
        return null
      }
      if (!res.ok) throw new Error(`Session refresh failed (${res.status})`)
      const data = (await res.json()) as RefreshResult
      localStorage.setItem(TOKEN_KEY, data.access_token)
      return data
    })().finally(() => {
      inFlight = null
    })
  }
  return inFlight
}

export async function endSession(): Promise<void> {
  localStorage.removeItem(TOKEN_KEY)
  try {
    await nativeFetch(`${AUTH_BASE}/staff/logout`, { method: 'POST', credentials: 'include' })
  } catch {
    /* signing out locally is enough if the server is unreachable */
  }
}

function bearerOf(input: RequestInfo | URL, init?: RequestInit): string | null {
  const h = new Headers(init?.headers ?? (input instanceof Request ? input.headers : undefined))
  const v = h.get('Authorization')
  return v && v.startsWith('Bearer ') ? v : null
}

function urlOf(input: RequestInfo | URL): string {
  if (typeof input === 'string') return input
  if (input instanceof URL) return input.href
  return input.url
}

// Seconds since the token in a "Bearer ..." header was issued (JWT iat), or null.
function tokenAgeSeconds(bearer: string): number | null {
  try {
    const part = bearer.slice(7).split('.')[1].replace(/-/g, '+').replace(/_/g, '/')
    const iat = JSON.parse(atob(part)).iat
    return typeof iat === 'number' ? Date.now() / 1000 - iat : null
  } catch {
    return null
  }
}

// A 401 for a token issued moments ago is not an expired session: the backend
// or its auth check failed for another reason (cold start, overload). Report it
// as a temporary error so no module treats it as "signed out".
function temporaryAuthError(): Response {
  return new Response(JSON.stringify({ detail: 'Temporary authorization error. Please retry.' }), {
    status: 503,
    headers: { 'Content-Type': 'application/json' },
  })
}

export function installFetchInterceptor(): void {
  if (installed) return
  installed = true

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const preview = previewToken()
    if (preview && bearerOf(input, init)) {
      const method = (init?.method ?? (input instanceof Request ? input.method : 'GET')).toUpperCase()
      if (method !== 'GET' && method !== 'HEAD' && !/\/staff\/auth\b/.test(urlOf(input))) return previewBlocked()
      const headers = new Headers(init?.headers ?? (input instanceof Request ? input.headers : undefined))
      headers.set('Authorization', `Bearer ${preview}`)
      const res = await nativeFetch(input instanceof Request ? input.clone() : input, { ...init, headers })
      if (res.status === 401) onPreviewExpired?.() // 30 minutes are over: back to the person's own view
      return res
    }
    // Keep a copy of a Request so it can be sent again (its body is single-use).
    const retryable = input instanceof Request ? input.clone() : input
    const res = await nativeFetch(input, init)
    if (res.status !== 401) return res
    const sent = bearerOf(input, init)
    if (!sent) return res
    if (/\/staff\/(refresh|logout|auth-code)\b/.test(urlOf(input))) return res
    const age = tokenAgeSeconds(sent)
    if (age !== null && age < 120) return temporaryAuthError()

    let session: RefreshResult | null
    try {
      session = await refreshSession()
    } catch {
      return res // refresh service unreachable: surface the original error, stay signed in
    }
    if (!session) {
      onExpired?.()
      return res
    }

    const headers = new Headers(init?.headers ?? (retryable instanceof Request ? retryable.headers : undefined))
    headers.set('Authorization', `Bearer ${session.access_token}`)
    const retried = await nativeFetch(retryable, { ...init, headers })
    // Only a refused refresh (above) signs the person out - never a retry.
    return retried.status === 401 ? temporaryAuthError() : retried
  }
}
